import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

interface CountdownModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const FADE_IN_MS = 400;
const COUNT_INTERVAL_MS = 1000;
const GO_DURATION_MS = 500;
const FADE_OUT_MS = 300;

const CountdownModal: React.FC<CountdownModalProps> = ({ isOpen, onComplete }) => {
  const [countdown, setCountdown] = useState(5);
  const [phase, setPhase] = useState<'fade-in' | 'count' | 'go' | 'fade-out'>('fade-in');
  const [isVisible, setIsVisible] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!isOpen) {
      setCountdown(5);
      setPhase('fade-in');
      setIsVisible(false);
      return;
    }

    // Start fade-in
    setIsVisible(true);

    const fadeInDone = setTimeout(() => {
      setPhase('count');
    }, FADE_IN_MS);

    return () => clearTimeout(fadeInDone);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || phase !== 'count') return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setPhase('go');
          return 0;
        }
        return prev - 1;
      });
    }, COUNT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isOpen, phase]);

  useEffect(() => {
    if (!isOpen || phase !== 'go') return;

    const goDone = setTimeout(() => {
      setPhase('fade-out');
      setIsVisible(false);
    }, GO_DURATION_MS);

    return () => clearTimeout(goDone);
  }, [isOpen, phase]);

  useEffect(() => {
    if (!isOpen || phase !== 'fade-out') return;

    const fadeOutDone = setTimeout(() => {
      onCompleteRef.current();
    }, FADE_OUT_MS);

    return () => clearTimeout(fadeOutDone);
  }, [isOpen, phase]);

  if (!isOpen) return null;

  const displayText = phase === 'go' || countdown === 0 ? 'Go!' : countdown;

  return createPortal(
    <div
      className={`quiz-countdown-overlay ${isVisible ? 'quiz-countdown-overlay--visible' : ''}`}
      aria-live="polite"
      aria-label={`Countdown: ${displayText}`}
    >
      <div className="quiz-countdown-content">
        <div className="quiz-countdown-number">{displayText}</div>
      </div>
    </div>,
    document.body
  );
};

export default CountdownModal;
